import { Field } from '@/components/ui/field';
import BrandVatNumberField from '@/pages/settings/components/BrandVatNumberField';
import { Input, NativeSelect, Stack } from '@chakra-ui/react';
import { CountrySelect } from '@fluenceur/ui';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface BillingTaxProfileValues {
  companyName: string;
  businessType: string;
  registrationNumber: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  state: string;
  vatNumber: string;
}

export function BillingTaxProfileFields({
  form,
  disabled,
  fixedCountry,
}: {
  form: UseFormReturn<BillingTaxProfileValues>;
  disabled: boolean;
  fixedCountry?: string;
}) {
  const { t, i18n } = useTranslation();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;
  const country = fixedCountry ?? watch('country');
  const vatNumber = watch('vatNumber');
  const fields = [
    ['companyName', 'billing.taxProfile.companyName', true, 255],
    ['registrationNumber', 'settings.registrationNumber', false, 255],
    ['addressLine1', 'settings.brandInformationForm.addressLine1', true, 255],
    ['addressLine2', 'settings.brandInformationForm.addressLine2', false, 255],
    ['postalCode', 'settings.postalCode', true, 20],
    ['city', 'settings.city', true, 255],
  ] as const;
  return (
    <Stack gap={4}>
      {fields.map(([name, label, required, maxLength]) => (
        <Field
          key={name}
          label={t(label)}
          required={required}
          invalid={!!errors[name]}
          errorText={errors[name]?.message}
        >
          <Input {...register(name)} variant="outline" maxLength={maxLength} disabled={disabled} />
        </Field>
      ))}
      <Field
        label={t('billing.taxProfile.businessType')}
        required
        invalid={!!errors.businessType}
        errorText={errors.businessType?.message}
      >
        <NativeSelect.Root disabled={disabled} variant="outline">
          <NativeSelect.Field
            {...register('businessType')}
            placeholder={t('billing.taxProfile.selectBusinessType')}
          >
            <option value="company">{t('billing.taxProfile.company')}</option>
            <option value="individual">{t('billing.taxProfile.individual')}</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field>
      <Field
        label={t('settings.country')}
        required
        invalid={!!errors.country}
        errorText={errors.country?.message}
        helperText={fixedCountry ? t('billing.taxProfile.countryFrozen') : undefined}
      >
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <CountrySelect
              name={field.name}
              value={country || undefined}
              onValueChange={field.onChange}
              variant="outline"
              disabled={disabled || !!fixedCountry}
              locale={i18n.language}
              placeholder={t('settings.brandInformationForm.selectCountry')}
              emptyText={t('onboarding.billing.noCountries')}
            />
          )}
        />
      </Field>
      {(country === 'US' || country === 'CA') && (
        <Field
          label={t('billing.taxProfile.state')}
          invalid={!!errors.state}
          errorText={errors.state?.message}
        >
          <Input {...register('state')} variant="outline" maxLength={255} disabled={disabled} />
        </Field>
      )}
      <fieldset disabled={disabled} style={{ border: 0, margin: 0, padding: 0 }}>
        <BrandVatNumberField
          country={country}
          value={vatNumber}
          registration={register('vatNumber')}
        />
      </fieldset>
    </Stack>
  );
}
